// Simulation clock control in the top bar. Lets Ken feel a whole Sunday in
// a minute: play, pause, speed, and jump to moments of the week. Drawn
// through the kit (LOOKS.md Part 7).

import { JUMP_PRESETS, useSim, type SimScenario, type SimSpeed } from '../data/SimProvider';
import { formatInZone } from '@fsp/domain';
import { deviceZone, useReaderZone } from '../data/hooks';
import { Hint, Popover } from '../kit/overlays';
import { Button, Caption, IconButton, Line, Segments, Stack, Text } from '../kit/ui';

/** The compact clock in the top bar: the reader's time, too narrow for its zone's name. */
function fmtShort(ms: number, zone: string): string {
  return new Date(ms).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: zone,
  });
}

/** The simulated moment in the reader's zone, named (B-181). */
function fmt(ms: number, zone: string): string {
  return formatInZone(ms, zone, { weekday: 'short', hour: 'numeric', minute: '2-digit' });
}

const SPEEDS = [
  { id: '1', label: 'Real time' },
  { id: '30', label: '30×' },
  { id: '300', label: '5 min/s' },
] as const;

const SEASONS = [
  { id: 'regular', label: 'Week 3' },
  { id: 'playoffs', label: 'Playoffs (16)' },
  { id: 'championship', label: 'Final (17)' },
] as const;

export function SimClock({ compact = false }: { compact?: boolean }) {
  const sim = useSim();
  const zone = useReaderZone() ?? deviceZone();
  return (
    <Popover
      width={300}
      target={
        <Hint label="Simulation clock">
          <Button variant="quiet" size="xs" icon="play-at">
            <Text numeric>
              {compact ? fmtShort(sim.now, zone).replace(/\s?[AP]M$/, '') : fmt(sim.now, zone)}
            </Text>
          </Button>
        </Hint>
      }
    >
      <Stack gap={3}>
        <Text tone="secondary" size="xs" block>
          Simulated week. Everything on screen is computed from this clock.
        </Text>
        <Line gap={2}>
          <IconButton
            icon={sim.playing ? 'pause' : 'play'}
            label={sim.playing ? 'Pause' : 'Play'}
            onClick={() => sim.setPlaying(!sim.playing)}
          />
          <Segments
            label="Speed"
            items={SPEEDS}
            value={String(sim.speed) as (typeof SPEEDS)[number]['id']}
            onChange={(v) => sim.setSpeed(Number(v) as SimSpeed)}
          />
        </Line>
        <Caption>Season</Caption>
        <Segments
          label="Season"
          items={SEASONS}
          value={sim.scenario}
          onChange={(v) => sim.setScenario(v as SimScenario)}
        />
        <Caption>Jump to</Caption>
        <Stack gap={1}>
          {JUMP_PRESETS.map((p) => (
            <Button key={p.id} size="xs" full onClick={() => sim.jump(p.id)}>
              <span className="fsp-simclock-jump">
                <span>{p.label}</span>
                <Text tone="secondary" size="xs">
                  {p.hint}
                </Text>
              </span>
            </Button>
          ))}
        </Stack>
        <Button
          variant="quiet"
          size="xs"
          onClick={() => sim.reseed(Math.floor(Math.random() * 1e6))}
        >
          New random league
        </Button>
      </Stack>
    </Popover>
  );
}
